import { useState, useCallback } from 'react';
import styles from './NewPasswordModal.module.scss';
import Modal from 'src/components/modal/Modal';
import Button from 'src/components/button/Button';
import { TextInput, TextInputValue } from 'src/components/text-input/TextInput';
import Spinner from '@src/components/spinner/Spinner';
import { StudyPlan, useHeaSetPasswordAfterLoginMutation } from '@src/api/graphql-codegen';
import {useAuthToken} from '@hooks/useAuthToken';
import { validatePassword, isEmptyObject } from '@utils/validation';

type Props = {
  open: boolean;
  studyPlan?: StudyPlan;
  onClose: () => void;
  onSuccess: () => void;
};

const emptyErrors = {
  password: '',
  confirmPassword: ''
};

function NewPasswordModal(props: Props) {
  const { open, studyPlan, onClose, onSuccess } = props;
  const [ password, setPassword ] = useState("")
  const [ confirmPassword, setConfirmPassword ] = useState("")
  const [beError, setBeError] = useState('');
  const [errors, setErrors] = useState(emptyErrors);
  const { setAuthPassword } = useAuthToken();

  const [heaSetPasswordAfterLogin, { loading }] = useHeaSetPasswordAfterLoginMutation({
    onCompleted: () => {
      setAuthPassword(password);
      onSuccess();
    },
    onError: (e) => {
      setBeError(e.message);
    }
  });

  const handleChangeText = useCallback((setter, key: keyof typeof emptyErrors) => (value: TextInputValue) => {
    setErrors({
      ...errors,
      [key]: '',
    });
    setter(value)
    setBeError('')
  }, [errors]);

  const handleSave = useCallback(() => {

    const errors: any = {};

    if (validatePassword(password)) {
      errors.password = validatePassword(password)
    }

    if (validatePassword(confirmPassword)) {
      errors.confirmPassword = validatePassword(confirmPassword)
    }

    if (isEmptyObject(errors)) {
      setErrors(emptyErrors);
      setBeError('');

      if (password && confirmPassword) {
        if (password !== confirmPassword) {
          setBeError('Password and confirm password should be equal.');
          return;
        }
      }

      heaSetPasswordAfterLogin({
        variables: {
          input: {
            password: password,
            studyplanId: studyPlan?.id || ''
          }
        }
      });

    } else {
      setErrors(errors);
    }
  }, [setBeError, heaSetPasswordAfterLogin, password, confirmPassword, studyPlan])

  return (
    <Modal global={false} open={open} onClose={() => {}} closeButton={false} className={styles.wrapper} closeOnOverlayClick={loading ? false : true}>
      <div>
        <div className={styles.header}>
          <div className={styles.normalText}>Before you activate a Study Plan...</div>
          <div className={styles.title}>Finish setting up your Account</div>
        </div>
        <div className={styles.content}>
          <div className={styles.contentRow}>Create your Password:</div>
          <TextInput allowSpecialCharacters id="password" hasError={!!errors.password} subTitle={errors.password} type="password" placeholder={'Type your password'} value={password} label={''} onChange={handleChangeText(setPassword, 'password')} />
          <TextInput allowSpecialCharacters id="confirm-password" hasError={!!errors.confirmPassword} subTitle={errors.confirmPassword} type="password" placeholder={'Re-type your password'} value={confirmPassword} label={''} onChange={handleChangeText(setConfirmPassword, 'confirmPassword')} />

          {Boolean(beError) && <div className={styles.error}>{beError}</div>}
          <div className={styles.action}>
            <Button disabled={loading} size="small" className={styles.cancelButton} onClick={onClose} label={'Cancel'}/>
            <Button disabled={loading} size="small" leftIcon={loading ? <Spinner width={20} height={20} /> : undefined} className={styles.startButton} onClick={handleSave} label={'Create Password'}/>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default NewPasswordModal;
